import React, { lazy } from 'react';
import IconSetting from '@/assets/images/menu/icon-setting.react.svg?react';
import { AuthNodes } from '@/config/app/auth';
import { MultiApp } from '@/config/context';
import { isInternal } from '@/infrastructure/auth';
import { CapitalPlatform, Platform } from '@/infrastructure/utils/app';


import { MenuIcon } from '@/presentation/components/menu-icon';
import { stores } from '@/presentation/store';
import { IRouteConfig } from '../types';

export const settingRoutes = (): IRouteConfig[] => {
  const { pid } = stores.ProductStore;
  const isHideMenu = pid === `${Platform.Hippy}`
    || pid === `${Platform.Kuikly}`
    || pid === `${Platform.MiniProgram}`
    || pid === `${Platform.Electron}`
    || pid === `${Platform.ReactNative}`;

  return [
    {
      path: '/setting',
      name: '设置',
      icon: <MenuIcon icon={IconSetting} fontSize={16} />,
      sort: 10,
      routes: [
        {
          path: '/setting/product/resource',
          name: '用量明细',
          component: lazy(() => import('@/presentation/pages/product/resource')),
          hideInMenu: true,
        },
        {
          path: '/epc/crash-trend',
          name: 'epc',
          component: lazy(() => import('@/presentation/pages/epc/crash-trend')),
          hideInMenu: true,
        },
        {
          path: '/setting/product',
          name: '产品信息',
          component: lazy(() => import('@/presentation/pages/product/info')),
        },
        {
          path: '/setting/members',
          name: '成员管理',
          versions: [MultiApp.WOA, MultiApp.Internal],
          component: lazy(() => {
            if (isInternal()) {
              return import('@/presentation/pages/product/internal-member');
            }
            return import('@/presentation/pages/product/member');
          }),
        },
        {
          path: '/setting/members',
          name: '成员管理',
          versions: [MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu],
          component: lazy(() => import('@/presentation/pages/product/internal-member-pro')),
        },
        {
          path: '/setting/config-hippy',
          name: '配置管理',
          component: lazy(() => import('@/presentation/pages/config-hippy')),
          platforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
            CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
          ],
        },
        {
          path: '/setting/sdk',
          name: 'SDK配置',
          flag: 'subMenu',
          hideInMenu: isHideMenu,
          component: lazy(() => import('@/presentation/pages/setting/app-deliver-config-pro')),
          versions: [MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu],
          excludePlatforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
            CapitalPlatform.Linux,
            CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
          ],
        },
        {
          path: '/setting/sdk',
          name: 'SDK配置',
          flag: 'subMenu',
          versions: [MultiApp.WOA, MultiApp.Internal],
          excludePlatforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
          ],
          routes: [
            {
              path: '/setting/app-deliver',
              name: '功能配置',
              component: lazy(() => import('@/presentation/pages/setting/app-deliver-config')),
            },
            {
              excludePlatforms: [CapitalPlatform.Flutter, CapitalPlatform.HarmonyOS],
              path: '/setting/compliance-app-deliver',
              name: '合规配置',
              component: lazy(() => import('@/presentation/pages/setting/app-deliver-config')),
            },
            {
              versions: [MultiApp.WOA],
              excludePlatforms: [CapitalPlatform.HarmonyOS],
              path: '/setting/compliance-app-deliver-by-shiply',
              name: '合规配置(新)',
              component: lazy(() => import('@/presentation/pages/setting/compliance-config')),

            },
          ],
        },
        {
          // 暂时对electron平台做隐藏，待开发
          path: '/setting/alert',
          name: '告警',
          component: lazy(() => import('@/presentation/pages/setting/alert-task')),
          versions: [MultiApp.WOA, MultiApp.Internal, MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu],
          excludePlatforms: [
            CapitalPlatform.Linux,
            CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
            CapitalPlatform.Electron,
          ],
        },
        {
          path: '/setting/tapd-auth',
          name: 'TAPD',
          versions: [MultiApp.WOA, MultiApp.Internal],
          excludePlatforms: [CapitalPlatform.Hippy, CapitalPlatform.Kuikly, CapitalPlatform.MiniProgram],
          whitelisted: true,
          component: lazy(() => import('@/presentation/pages/setting/tapd-setting/auth')),
          hideInMenu: true,
        },
        {
          path: '/setting/version',
          name: '应用版本',
          excludePlatforms: [CapitalPlatform.Hippy, CapitalPlatform.Kuikly, CapitalPlatform.MiniProgram],
          versions: [MultiApp.WOA, MultiApp.Internal],
          component: lazy(() => import('@/presentation/pages/setting/version')),
        },
        {
          path: '/setting/dsyms',
          name: '符号表',
          component: lazy(() => import('@/presentation/pages/setting/symbol-manage')),
          excludePlatforms: [
            CapitalPlatform.ReactNative,
          ],
        },
        {
          path: '/setting/auto-fix-reports',
          name: '自动修复报告',
          component: lazy(() => import('@/presentation/pages/setting/auto-fix-reports')),
          exact: true,
        },
        {
          path: '/setting/auto-fix-reports/detail',
          name: '自动修复报告详情',
          component: lazy(() => import('@/presentation/pages/setting/auto-fix-reports/detail')),
          hideInMenu: true,
        },
        {
          path: '/setting/auto-fix-reports/issue',
          name: '自动修复详情',
          component: lazy(() => import('@/presentation/pages/setting/auto-fix-reports/issue')),
          hideInMenu: true,
        },
        {
          name: 'Issue 聚类配置',
          path: '/setting/cluster-config',
          authName: AuthNodes.ClusterConfig,
          versions: [MultiApp.WOA, MultiApp.Internal],
          excludePlatforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
            CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
          ],
          component: lazy(() => import('@/presentation/pages/setting/cluster-config')),
        },
        {
          name: 'Issue 聚类配置',
          path: '/setting/cluster-config',
          versions: [MultiApp.Pro, MultiApp.Dji, MultiApp.Sgp, MultiApp.Eu],
          excludePlatforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
            CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
          ],
          component: lazy(() => import('@/presentation/pages/setting/cluster-config-pro')),
          disabledByFree: true,
        },
        {
          name: '产品配置',
          excludePlatforms: [
            CapitalPlatform.Hippy,
            CapitalPlatform.Kuikly,
            CapitalPlatform.MiniProgram,
            // CapitalPlatform.Electron,
            CapitalPlatform.ReactNative,
          ],
          path: '/setting/product-config',
          component: lazy(() => import('@/presentation/pages/setting/product-config')),
        },
        {
          name: '工蜂授权',
          hideInMenu: true,
          path: '/git/oauth',
          component: lazy(() => import('@/presentation/pages/setting/oauth')),
          whitelisted: true,
        },
      ],
    },
  ];
};

export default settingRoutes;
